import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getBugs, getBugsByProjectId } from "../../services/bugService";
import { formatDate } from "../../utils/formatDate";
import PriorityBadge from "../../components/PriorityBadge";

import {
  PencilSquareIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

/* ================== ADDED HELPERS (NO REMOVALS) ================== */
const isResolved = (status) => status === "resolved";

const resolvedStyles = {
  card: "bg-green-50 border-l-4 border-green-500 opacity-75",
  row: "bg-green-50 opacity-75",
  badge: "bg-green-100 text-green-700",
};
/* ================================================================ */

const BugList = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ================== ADDED FILTER STATE ================== */
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  /* ======================================================== */

  useEffect(() => {
    async function fetchBugs() {
      try {
        const data =
          projectId !== undefined
            ? await getBugsByProjectId(projectId)
            : await getBugs();
        setBugs(data);
      } catch (error) {
        console.error("Failed to fetch bugs:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchBugs();
  }, [projectId]);

  /* ================== DERIVED FILTER DATA ================== */
  const projectOptions = useMemo(() => {
    const unique = new Map();
    bugs.forEach((b) => unique.set(b.projectTitle, b.projectTitle));
    return Array.from(unique.values());
  }, [bugs]);

  const filteredBugs = useMemo(() => {
    return bugs.filter((bug) => {
      const projectMatch =
        projectFilter === "all" || bug.projectTitle === projectFilter;

      const statusMatch =
        statusFilter === "all" || bug.status === statusFilter;

      return projectMatch && statusMatch;
    });
  }, [bugs, projectFilter, statusFilter]);
  /* ========================================================= */

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="p-0 md:p-4">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Bug Reports</h1>

        <button
          onClick={() => navigate("/bugs/add")}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          + Report Bug
        </button>
      </div>

      {/* ================= FILTER BAR ================= */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-4 items-end">
        {/* Project Filter */}
        <div>
          <label className="block text-sm font-medium mb-1">Project</label>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="border px-3 py-2 rounded w-48"
          >
            <option value="all">All Projects</option>
            {projectOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border px-3 py-2 rounded w-40"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>

        {/* Count */}
        <div className="text-sm text-gray-600 ml-auto">
          Showing <strong>{filteredBugs.length}</strong> of{" "}
          <strong>{bugs.length}</strong>
        </div>
      </div>
      {/* ================================================= */}

      {filteredBugs.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          No bugs match the selected filters.
        </div>
      ) : (
        <>
          {/* ================= MOBILE VIEW ================= */}
          <div className="space-y-4 md:hidden">
            {filteredBugs.map((bug) => (
              <div
                key={bug.id}
                className={`rounded-xl shadow-sm p-4 space-y-2 ${
                  isResolved(bug.status)
                    ? resolvedStyles.card
                    : "bg-white"
                }`}
              >
                <div className="flex justify-between items-start">
                  <h2 className="font-semibold text-lg">{bug.title}</h2>
                  <PriorityBadge priority={bug.priority} />
                </div>

                <p className="text-sm text-gray-600">
                  <strong>Project:</strong> {bug.projectTitle}
                </p>

                <div className="flex items-center gap-2 text-sm">
                  <strong>Status:</strong>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      isResolved(bug.status)
                        ? resolvedStyles.badge
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {bug.status}
                  </span>
                </div>

                <p className="text-sm text-gray-500">
                  Reported on {formatDate(bug.createdAt)}
                </p>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => navigate(`/bugs/${bug.id}/edit`)}
                    className="p-2 rounded hover:bg-gray-100"
                  >
                    <PencilSquareIcon className="h-5 w-5 text-blue-600" />
                  </button>

                  <button
                    onClick={() => navigate(`/bugs/${bug.id}`)}
                    className="p-2 rounded hover:bg-gray-100"
                  >
                    <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ================= DESKTOP TABLE ================= */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full bg-white rounded shadow">
              <thead>
                <tr className="bg-gray-100 text-gray-700 text-left">
                  <th className="p-4">#</th>
                  <th className="p-4">Title</th>
                  <th className="p-4">Project</th>
                  <th className="p-4">Priority</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Reported On</th>
                  <th className="p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBugs.map((bug, index) => (
                  <tr
                    key={bug.id}
                    className={`border-t ${
                      isResolved(bug.status)
                        ? resolvedStyles.row
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="p-4">{index + 1}</td>
                    <td className="p-4">{bug.title}</td>
                    <td className="p-4">{bug.projectTitle}</td>
                    <td className="p-4">
                      <PriorityBadge priority={bug.priority} />
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          isResolved(bug.status)
                            ? resolvedStyles.badge
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {bug.status}
                      </span>
                    </td>
                    <td className="p-4">
                      {formatDate(bug.createdAt)}
                    </td>
                    <td className="p-4 space-x-2">
                      <button
                        onClick={() =>
                          navigate(`/bugs/${bug.id}/edit`)
                        }
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <PencilSquareIcon className="h-5 w-5 text-blue-600" />
                      </button>

                      <button
                        onClick={() => navigate(`/bugs/${bug.id}`)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BugList;
